import { useState } from 'react';
import { ChevronUp } from 'lucide-react';
import { Button } from '@ooh/ui/components/button';
import { Popover, PopoverContent, PopoverTrigger } from '@ooh/ui/components/popover';
import type {
  ClientOfferReviewCustomPeriodItem,
  ClientOfferReviewFooterBarLabels,
} from './client-offer-review-types';

/**
 * Per-unit breakdown of every non-default period selection, opened from a chip
 * in the sticky footer bar.
 *
 * Replaces the v1 `ClientOfferSelectedPeriodsSummary` slab the v2 shell used to
 * render below the list/map split: that block re-stated the accepted/rejected/
 * pending totals already shown in the footer and the summary stats, and sat
 * outside the page's visual flow, so clients only found it by scrolling past
 * the map. It lives in the footer rather than the toolbar because the footer is
 * the "what am I about to submit" surface and has the room — the toolbar is
 * already at capacity. `ClientOfferSelectedPeriodsSummary` itself stays; the v1
 * `client-offer-page` route still renders it.
 *
 * Also hosts the "Wyślij zapytanie" period-inquiry submit action
 * (client-offer-period-inquiry / Period Inquiry Submission Action) — moved
 * here from the retired unit detail modal. This popover already lists
 * exactly the units whose selection differs from the campaign default,
 * which is the precondition for offering the action at all, so `periodInquiry`
 * renders only when provided — `items` being non-empty is already this whole
 * popover's own render guard.
 */
export function ClientOfferReviewCustomPeriodsPopover({
  items,
  onRestoreDefaultPeriods,
  onSelectUnit,
  labels,
  periodInquiry,
}: {
  items: ClientOfferReviewCustomPeriodItem[];
  onRestoreDefaultPeriods: () => void;
  /** Scrolls the list to that unit's card and flies the map to it. */
  onSelectUnit: (unitId: string) => void;
  labels: ClientOfferReviewFooterBarLabels;
  /** Present only on interactive routes (SSO + public) — absent on the read-only version-preview screen. */
  periodInquiry?: { submitLabel: string; onSubmit: () => void; isPending: boolean };
}) {
  // Controlled so picking a row can dismiss the panel — it overlaps the unit
  // list it just scrolled to, so leaving it open hides the thing you asked for.
  const [open, setOpen] = useState(false);

  if (items.length === 0) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          aria-label={labels.customPeriodsCount(items.length)}
          className="h-9 gap-2 rounded-lg border-border bg-muted/50 px-2.5 text-xs font-medium"
          data-testid="custom-periods-trigger"
        >
          <span className="rounded-md bg-foreground/10 px-1.5 py-0.5 text-2xs font-semibold tabular-nums">
            {items.length}
          </span>
          {labels.customPeriodsChip}
          <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={2} />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        side="top"
        align="start"
        className="w-96 p-0"
        data-testid="custom-periods-popover"
      >
        <div className="border-b border-border/60 px-3 py-2">
          <span className="text-xs font-semibold text-foreground">{labels.customPeriodsTitle}</span>
        </div>

        <ul className="max-h-64 overflow-y-auto py-1">
          {items.map((item) => (
            <li key={item.id}>
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  onSelectUnit(item.id);
                  setOpen(false);
                }}
                className="h-auto w-full flex-col items-stretch justify-start gap-0.5 whitespace-normal rounded-none px-3 py-2 text-left font-normal"
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className="truncate text-xs font-medium text-foreground">
                    {item.address}
                  </span>
                  <span className="shrink-0 text-2xs text-muted-foreground">{item.city}</span>
                </div>
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-2xs text-foreground">{item.periodSummary}</span>
                  <span className="shrink-0 font-mono text-2xs text-muted-foreground">
                    {item.grw}
                  </span>
                </div>
              </Button>
            </li>
          ))}
        </ul>

        <div className="flex flex-col gap-1.5 border-t border-border/60 px-3 py-2.5">
          <span className="text-2xs text-muted-foreground">{labels.customPeriodsRestoreHint}</span>
          <Button
            type="button"
            variant="outline"
            onClick={onRestoreDefaultPeriods}
            data-testid="restore-default-periods"
            className="h-8 rounded-lg text-xs"
          >
            {labels.restoreCustomPeriods}
          </Button>
          {periodInquiry ? (
            <Button
              type="button"
              variant="outline"
              onClick={periodInquiry.onSubmit}
              disabled={periodInquiry.isPending}
              data-testid="period-inquiry-submit"
              className="h-8 rounded-lg text-xs"
            >
              {periodInquiry.submitLabel}
            </Button>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
}
